import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, untracked, viewChild } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { PlatformChartNoteAttachmentDto } from '@data/_generated-api/platform-api/models/platform-chart-note-attachment-dto';
import { PlatformChartRelationDto } from '@data/_generated-api/platform-api/models/platform-chart-relation-dto';
import { PlatformChartType } from '@data/_generated-api/platform-api/models/platform-chart-type';
import { SupportCommentComposerComponent, SupportCommentPayload } from '@/app/support/support-comment-composer.component';
import { renderSupportMarkup } from '@/app/support/support-markup';
import { HdChartGroupService } from '../../library/hd-chart-group.service';
import { HdChartLibraryService } from '../../library/hd-chart-library.service';
import { HdChartDataCache } from '../../library/hd-chart-data-cache.service';
import { HdCacheEntry } from '../../library/hd-keyed-cache';
import { HD_CHART_TYPE_ICONS } from '../../library/hd-chart-type-metadata';
import {
  hdRelationTypeIcon,
  hdRelationTypeLabel,
  isHdSystemRelationType,
} from '../../library/hd-chart-relation-metadata';
import { HdMomentsExportDialogComponent, HdMomentsExportTarget } from '../../moments-export/hd-moments-export-dialog.component';
import { HdContentLanguageService } from '../hd-content-language.service';

/** Eén link-rij in het Notities-paneel: de relatie + weergavevelden. */
interface NoteLinkRowVm {
  readonly relation: PlatformChartRelationDto;
  readonly icon: string;
  readonly typeLabel: string;
  readonly name: string;
  readonly hasNote: boolean;
  readonly attachmentCount: number;
  readonly noteHtml: string;
}

/**
 * Vast "Notities"-dockpaneel ná Editor (spec notable moments §6, visie Steven
 * 2026-09-16): de eigen notities van de geopende chart, plus per link de
 * notities van die link — achter een klik op de link, zodat de lijst compact
 * blijft en links zonder notitie er meteen één kunnen krijgen. Invoer met de
 * support-composer (sumiere opmaak + afbeeldingen).
 */
@Component({
  selector: 'hd-notes-panel',
  standalone: true,
  imports: [SharedModule, SupportCommentComposerComponent, HdMomentsExportDialogComponent],
  templateUrl: './hd-notes-panel.component.html',
  styleUrl: './hd-notes-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdNotesPanelComponent {
  /** Library-chart-id van de actieve tab; null = niet opgeslagen → eerst opslaan. */
  readonly savedChartId = input<string | null>(null);
  readonly chartName = input('');

  /** Klik op een linknaam: open die chart (de page laadt hem in een nieuwe tab). */
  readonly chartOpenRequested = output<string>();

  /**
   * Toon de "losmaken"-knop (enkel in de vaste Notities-docktab): de pagina
   * opent de notities dan als zwevend venster, zodat de rest van het
   * rechterpaneel zichtbaar wordt om uit te kopiëren.
   */
  readonly undockable = input(false);
  readonly undockRequested = output<void>();

  private readonly library = inject(HdChartLibraryService);
  private readonly groupService = inject(HdChartGroupService);
  /**
   * Notitie, links en bijlagen komen uit de per-chart datacache (spec 2026-10-06): een tabwissel naar
   * een bezochte chart doet geen calls, en eigen mutaties via de services werken de cache zelf bij.
   */
  private readonly dataCache = inject(HdChartDataCache);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /** Enkel koud laden (niets te tonen); een stille verversing laat de inhoud staan. */
  protected readonly loading = computed(() => this.cacheEntries().some(entry => !!entry?.loading && entry.value === undefined));
  /** Fout van een eigen actie (opslaan, verwijderen). */
  private readonly actionError = signal('');
  protected readonly error = computed(() =>
    this.actionError() || (this.cacheEntries().find(entry => entry?.value === undefined && !!entry?.error)?.error ?? ''));

  /** Chart-notitie (GeneralNotes) + de bijlagen op het chart-anker. */
  protected readonly chartNotes = computed<string | null>(() =>
    this.dataCache.chartMeta.value(this.savedChartId())?.generalNotes ?? null);
  protected readonly chartAttachments = computed<PlatformChartNoteAttachmentDto[]>(() =>
    this.dataCache.noteAttachments.value(this.savedChartId()) ?? []);
  protected readonly editingChartNote = signal(false);
  protected readonly savingChartNote = signal(false);

  protected readonly relations = computed<PlatformChartRelationDto[]>(() =>
    this.dataCache.relations.value(this.savedChartId()) ?? []);
  /**
   * Deze chart is een moment of cycle van een andere chart: dan kan ze als moment naar Markdown
   * (spec 2026-10-21-dagboek-momenten-export-design). Gewone charts krijgen hun export later apart.
   */
  protected readonly isMoment = computed(() =>
    this.relations().some(relation => relation.relationType === 'momentOf' || relation.relationType === 'cycleOf'));
  protected readonly exportTarget = signal<HdMomentsExportTarget | null>(null);
  /** Links-sectie standaard dichtgeklapt (zelfde patroon als Relaties/Momenten). */
  protected readonly linksExpanded = signal(false);
  /** Filter: alleen links die een notitie of bijlagen dragen. */
  protected readonly onlyWithNotes = signal(false);
  protected readonly linksPage = signal(1);
  protected readonly linksPageSize = 10;
  /** Uitgeklapte link (notities zichtbaar); null = alles dicht. */
  protected readonly expandedRelationId = signal<string | null>(null);
  protected readonly editingRelationId = signal<string | null>(null);
  protected readonly savingRelationNote = signal(false);

  /** Data-url-cache per bijlage-id (thumbs + grote weergave). */
  private readonly attachmentUrls = signal<ReadonlyMap<string, string>>(new Map());
  private readonly pendingAttachmentUrls = new Set<string>();
  /** Grote weergave van één afbeelding; null = gesloten. */
  protected readonly viewerUrl = signal<string | null>(null);

  constructor() {
    effect(onCleanup => {
      const chartId = this.savedChartId();
      untracked(() => this.resetState());
      if (!chartId) return;
      const releases = [
        this.dataCache.chartMeta.watch(chartId),
        this.dataCache.relations.watch(chartId),
        this.dataCache.noteAttachments.watch(chartId),
      ];
      onCleanup(() => releases.forEach(release => release()));
    });
    // Thumbs: chart-bijlagen meteen, link-bijlagen zodra die link uitgeklapt is (ook na een mutatie).
    effect(() => {
      const attachments = this.chartAttachments();
      untracked(() => this.ensureUrlsFor(attachments));
    });
    effect(() => {
      const expandedId = this.expandedRelationId();
      const expanded = expandedId ? this.relations().find(candidate => candidate.id === expandedId) : undefined;
      untracked(() => this.ensureUrlsFor(expanded?.noteAttachments));
    });
  }

  private cacheEntries(): (HdCacheEntry<unknown> | undefined)[] {
    const chartId = this.savedChartId();
    return [
      this.dataCache.chartMeta.entry(chartId),
      this.dataCache.relations.entry(chartId),
      this.dataCache.noteAttachments.entry(chartId),
    ];
  }

  protected readonly text = computed(() => {
    const translate = this.contentTranslate();
    return {
      chartNotesTitle: translate('humanDesign.notesPanel.chartNotesTitle'),
      linksTitle: translate('humanDesign.notesPanel.linksTitle'),
      showLinks: translate('humanDesign.notesPanel.showLinks'),
      hideLinks: translate('humanDesign.notesPanel.hideLinks'),
      onlyWithNotes: translate('humanDesign.notesPanel.onlyWithNotes'),
      saveFirst: translate('humanDesign.notesPanel.saveFirst'),
      loading: translate('humanDesign.chartManager.detail.loading'),
      empty: translate('humanDesign.notesPanel.empty'),
      noLinks: translate('humanDesign.notesPanel.noLinks'),
      edit: translate('humanDesign.notesPanel.edit'),
      cancel: translate('humanDesign.notesPanel.cancel'),
      save: translate('humanDesign.notesPanel.save'),
      notePlaceholder: translate('humanDesign.notesPanel.notePlaceholder'),
      removeAttachment: translate('humanDesign.notesPanel.removeAttachment'),
      openChart: translate('humanDesign.notesPanel.openChart'),
      undock: translate('humanDesign.notesPanel.undock'),
      exportMoment: translate('humanDesign.momentsExport.buttonMoment'),
      undockWhileEditing: translate('humanDesign.notesPanel.undockWhileEditing'),
      hasNote: translate('humanDesign.advancedPanel.moments.hasNote'),
      hasAttachments: translate('humanDesign.advancedPanel.moments.hasAttachments'),
      closeViewer: translate('actions.close'),
    };
  });

  protected readonly chartNotesHtml = computed(() => renderSupportMarkup(this.chartNotes()));

  protected readonly linkRows = computed<NoteLinkRowVm[]>(() => {
    const translate = this.contentTranslate();
    return this.relations().map(relation => ({
      relation,
      icon: isHdSystemRelationType(relation.relationType)
        ? (relation.otherChartIcon
          || (HD_CHART_TYPE_ICONS[relation.otherChartType ?? PlatformChartType.Unknown]
            ?? HD_CHART_TYPE_ICONS[PlatformChartType.Unknown]))
        : hdRelationTypeIcon(relation.relationType),
      typeLabel: hdRelationTypeLabel(translate, relation.relationType),
      name: relation.otherChartName ?? '',
      hasNote: !!relation.note?.trim(),
      attachmentCount: relation.noteAttachments?.length ?? 0,
      noteHtml: renderSupportMarkup(relation.note),
    }));
  });

  protected readonly filteredLinkRows = computed<NoteLinkRowVm[]>(() =>
    this.onlyWithNotes()
      ? this.linkRows().filter(row => row.hasNote || row.attachmentCount > 0)
      : this.linkRows());

  /** Aantal links mét notitie of bijlagen: indicator in de (dichtgeklapte) sectieheader. */
  protected readonly linksWithNotesCount = computed(() =>
    this.linkRows().filter(row => row.hasNote || row.attachmentCount > 0).length);

  protected readonly pagedLinkRows = computed<NoteLinkRowVm[]>(() => {
    const start = (this.linksPage() - 1) * this.linksPageSize;
    return this.filteredLinkRows().slice(start, start + this.linksPageSize);
  });

  protected toggleLinksExpanded(): void {
    this.linksExpanded.update(expanded => !expanded);
  }

  protected setOnlyWithNotes(value: boolean): void {
    this.onlyWithNotes.set(value);
    this.linksPage.set(1);
  }

  // ── Chart-notitie ─────────────────────────────────────────────────────

  /** De composer van de chart-notitie, voor de save-knop in de sectieheader. */
  private readonly chartNoteComposer = viewChild<SupportCommentComposerComponent>('chartNoteComposer');

  /**
   * Losmaken kan niet tijdens het bewerken: het zwevende venster is een nieuwe
   * instantie, een lopend concept in de composer zou verloren gaan.
   */
  protected readonly isEditing = computed(() => this.editingChartNote() || this.editingRelationId() != null);

  protected requestUndock(): void {
    if (this.isEditing()) return;
    this.undockRequested.emit();
  }

  protected openExport(): void {
    const chartId = this.savedChartId();
    if (!chartId) return;
    this.exportTarget.set({ kind: 'moment', chartId });
  }

  protected startChartNoteEdit(): void {
    this.editingChartNote.set(true);
  }

  protected cancelChartNoteEdit(): void {
    this.editingChartNote.set(false);
  }

  /** Save-icoon in de sectieheader: zelfde verzending als de knop onderin de composer. */
  protected submitChartNoteFromHeader(): void {
    void this.chartNoteComposer()?.submit();
  }

  /** De composer van de uitgeklapte link (er is er hooguit één tegelijk in bewerken). */
  private readonly relationNoteComposer = viewChild<SupportCommentComposerComponent>('relationNoteComposer');

  /** Save-icoon rechtsboven in het linkdetail: zelfde verzending als de knop onderin de composer. */
  protected submitRelationNoteFromHeader(): void {
    void this.relationNoteComposer()?.submit();
  }

  protected async saveChartNote(payload: SupportCommentPayload): Promise<void> {
    const chartId = this.savedChartId();
    if (!chartId || this.savingChartNote()) return;
    this.actionError.set('');
    this.savingChartNote.set(true);
    try {
      const note = payload.body.trim() || null;
      // De services melden elke geslaagde stap aan de datacache: notitie en bijlagen verschijnen
      // zonder herlaad-call.
      await this.library.updateChartNotes(chartId, note);
      for (const attachment of payload.attachments) {
        await this.groupService.addNoteAttachment({ chartId }, { ...attachment });
      }
      this.editingChartNote.set(false);
    } catch (e) {
      this.actionError.set(e instanceof Error ? e.message : `${e}`);
    } finally {
      this.savingChartNote.set(false);
    }
  }

  protected async removeChartAttachment(id: string): Promise<void> {
    const chartId = this.savedChartId();
    if (!chartId) return;
    this.actionError.set('');
    try {
      await this.groupService.deleteNoteAttachment(id);
    } catch (e) {
      this.actionError.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  // ── Link-notities ─────────────────────────────────────────────────────

  protected toggleRelation(relationId: string | undefined): void {
    if (!relationId) return;
    this.expandedRelationId.update(current => (current === relationId ? null : relationId));
    this.editingRelationId.set(null);
  }

  protected startRelationNoteEdit(relationId: string | undefined): void {
    if (relationId) this.editingRelationId.set(relationId);
  }

  protected cancelRelationNoteEdit(): void {
    this.editingRelationId.set(null);
  }

  protected async saveRelationNote(relation: PlatformChartRelationDto, payload: SupportCommentPayload): Promise<void> {
    const chartId = this.savedChartId();
    if (!chartId || !relation.id || this.savingRelationNote()) return;
    this.actionError.set('');
    this.savingRelationNote.set(true);
    try {
      const note = payload.body.trim() || null;
      await this.groupService.updateRelationNote(relation.id, note);
      for (const attachment of payload.attachments) {
        await this.groupService.addNoteAttachment({ chartRelationId: relation.id }, { ...attachment });
      }
      this.editingRelationId.set(null);
    } catch (e) {
      this.actionError.set(e instanceof Error ? e.message : `${e}`);
    } finally {
      this.savingRelationNote.set(false);
    }
  }

  protected async removeRelationAttachment(id: string): Promise<void> {
    const chartId = this.savedChartId();
    if (!chartId) return;
    this.actionError.set('');
    try {
      await this.groupService.deleteNoteAttachment(id);
    } catch (e) {
      this.actionError.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  protected openLinkedChart(relation: PlatformChartRelationDto): void {
    if (relation.otherChartId) this.chartOpenRequested.emit(relation.otherChartId);
  }

  // ── Afbeeldingen ──────────────────────────────────────────────────────

  protected attachmentUrl(id: string | undefined): string | null {
    if (!id) return null;
    return this.attachmentUrls().get(id) ?? null;
  }

  protected openViewer(id: string | undefined): void {
    const url = this.attachmentUrl(id);
    if (url) this.viewerUrl.set(url);
  }

  protected closeViewer(): void {
    this.viewerUrl.set(null);
  }

  /** Data-urls aanvragen voor elke bijlage die er nog geen heeft (na laden, opslaan of verwijderen). */
  private ensureUrlsFor(attachments: readonly PlatformChartNoteAttachmentDto[] | null | undefined): void {
    attachments?.forEach(attachment => {
      if (attachment.id) void this.ensureAttachmentUrl(attachment.id);
    });
  }

  private async ensureAttachmentUrl(id: string): Promise<void> {
    // De thumb-effects lopen bij elke cachewijziging opnieuw: een lopende aanvraag niet verdubbelen.
    if (this.attachmentUrls().get(id) || this.pendingAttachmentUrls.has(id)) return;
    this.pendingAttachmentUrls.add(id);
    try {
      const content = await this.groupService.getNoteAttachmentContent(id);
      const url = `data:${content.contentType};base64,${content.dataBase64}`;
      this.attachmentUrls.update(map => new Map(map).set(id, url));
    } catch {
      // Thumb blijft dan gewoon leeg; de metadata-rij toont de bestandsnaam.
    } finally {
      this.pendingAttachmentUrls.delete(id);
    }
  }

  // ── Laden ─────────────────────────────────────────────────────────────

  /** UI-state per chart; de data zelf zit in de datacache. */
  private resetState(): void {
    this.actionError.set('');
    this.expandedRelationId.set(null);
    this.editingRelationId.set(null);
    this.editingChartNote.set(false);
    this.linksPage.set(1);
  }

  private contentTranslate(): (key: string) => string {
    this.contentLang.translationsLoadedTick();
    const lang = this.contentLang.effectiveLang();
    return (key: string) => this.transloco.translate<string>(key, undefined, lang);
  }
}
